import Image from 'next/image';
import Link from 'next/link';
import type { TeamDto } from '@/api/backoffice-schemas';
import { CheckIcon as Check } from '@/components/icons/CheckIcon';
import { TeamPlaceholderIcon as TeamLogoPlaceholder } from '@/components/icons/teams/TeamPlaceholderIcon';
import { cn } from '@/utils/cn';

interface TeamDisplayProps {
  team: TeamDto | undefined | null;
  isWinner: boolean;
  isSecondary?: boolean;
}

export const TeamDisplay = ({
  team,
  isWinner,
  isSecondary = false,
}: TeamDisplayProps) => {
  if (!team) {
    return (
      <div
        className={cn(
          'flex content-center items-center gap-2 md:w-[208px]',
          isSecondary ? 'flex-row md:flex-row-reverse' : 'flex-row'
        )}
      >
        <TeamLogoPlaceholder
          desc="Team logo placeholder icon"
          title="Team Logo"
        />
        <div
          className={cn(
            'flex items-center gap-1 md:gap-2',
            isSecondary ? 'flex-row md:flex-row-reverse' : 'flex-row'
          )}
        >
          <p className="font-medium text-[0.875rem] leading-4.5 md:hidden">
            TBD
          </p>
        </div>
      </div>
    );
  }

  const content = (
    <>
      {team.imageUrl ? (
        <Image
          alt={team.name || 'Team logo'}
          height={20}
          src={team.imageUrl}
          width={20}
        />
      ) : (
        <TeamLogoPlaceholder
          desc="Team logo placeholder icon"
          title="Team Logo"
        />
      )}
      <div
        className={cn(
          'flex items-center gap-1 md:gap-2',
          isSecondary ? 'flex-row md:flex-row-reverse' : 'flex-row'
        )}
      >
        <p className="hidden font-medium text-[0.875rem] leading-4.5 md:block">
          {team.name ?? '(TBD)'}
        </p>
        <p className="max-w-24 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-[0.875rem] leading-4.5 md:hidden">
          {team.acronym ?? team.name ?? '(TBD)'}
        </p>
        {isWinner && (
          <Check
            className="text-green"
            desc="Check mark icon for winning team"
            title="Winner"
          />
        )}
      </div>
    </>
  );

  const containerClassName = cn(
    'flex content-center items-center gap-2 md:w-[208px]',
    isSecondary ? 'flex-row md:flex-row-reverse' : 'flex-row'
  );

  if (team.slug) {
    return (
      <Link
        aria-label={`View ${team.name ?? 'team'} profile`}
        className={cn(containerClassName, 'transition-opacity hover:opacity-80')}
        href={`/teams/${team.slug}`}
      >
        {content}
      </Link>
    );
  }

  return <div className={containerClassName}>{content}</div>;
};
